import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Table,
  Button,
  Input,
  InputNumber,
  Select,
  DatePicker,
  message,
  Modal,
  Tooltip,
  Spin,
  Form,
  Dropdown,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  PrinterOutlined,
  ArrowLeftOutlined,
  UserAddOutlined,
  MailOutlined,
  DownloadOutlined,
  EditOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import axiosInstance from "../utils/axiosInstance";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const { Option } = Select;

const emptyInvoice = () => ({
  id: null,
  invoiceNo: null,
  status: "draft",
  issueDate: dayjs(),
  dueDate: null,
  reference: "",
  customerId: null,
  customer: { name: "", address: "", vat: "", email: "" },
  lines: [],
  note: "",
});

function useBootSelection() {
  const location = useLocation();
  const stateSel = location.state?.selectedOrderIds || [];
  const pickup = sessionStorage.getItem("invoice:addFromProduction");
  if (pickup) {
    try {
      const parsed = JSON.parse(pickup);
      sessionStorage.removeItem("invoice:addFromProduction");
      return parsed;
    } catch {}
  }
  return stateSel;
}

const normalizeLineTypeForFrontend = (lineType) => {
  const v = String(lineType || "").toLowerCase();
  if (v === "inventory" || v === "part") return "part";
  if (v === "manual") return "manual";
  return "production";
};

const normalizeLineTypeForPayload = (type) => {
  const v = String(type || "").toLowerCase();
  if (v === "part" || v === "inventory") return "inventory";
  if (v === "manual") return "manual";
  return "production";
};

const InvoiceManager = () => {
  const navigate = useNavigate();
  const params = useParams();
  const invoiceId = params.invoiceId ? Number(params.invoiceId) : null;
  const selectedAtStart = useBootSelection();

  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState("");
  const [invoice, setInvoice] = useState(emptyInvoice());
  const [inventoryParts, setInventoryParts] = useState([]);

  const [contacts, setContacts] = useState([]);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactForm] = Form.useForm();

  const [headerVatPercent, setHeaderVatPercent] = useState(0);
  const [previewInvoiceNo, setPreviewInvoiceNo] = useState("");

  const [partModalOpen, setPartModalOpen] = useState(false);
  const [addingPart, setAddingPart] = useState({ productId: null, qty: 1, price: 0 });

  const [prodModalOpen, setProdModalOpen] = useState(false);
  const [prodLoading, setProdLoading] = useState(false);
  const [prodOrders, setProdOrders] = useState([]);
  const [prodSelectedRowKeys, setProdSelectedRowKeys] = useState([]);
  const [prodExistingIds, setProdExistingIds] = useState([]);
  const prodExistingSet = useMemo(() => new Set(prodExistingIds.map(Number)), [prodExistingIds]);

  const [prodLookup, setProdLookup] = useState({});

  const [seller, setSeller] = useState({
    name: "",
    addressLines: [],
    email: "",
    phone: "",
    vat: "",
  });

  const [initialSnapshot, setInitialSnapshot] = useState("");

  const isExistingInvoice = !!invoiceId || !!invoice.id;

  const formatCurrency = (n) => {
    try {
      return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP",
        maximumFractionDigits: 2,
      }).format(Number(n || 0));
    } catch {
      return `£${Number(n || 0).toFixed(2)}`;
    }
  };

 const normalizePreviewInvoice = (v) => {
  if (v == null || v === "") return "";
  const s = String(v).trim();
  if (!s) return "";
  if (/^inv[-\s]/i.test(s) || /^INV-/i.test(s)) return s;
  if (/^\d+$/.test(s)) return `INV-${s}`;
  return s;
};

const formatProductionDateOrMonth = (record) => {
  if (!record) return "-";

  const orderDate = record.orderDate || record.order_date || record.createdAt || null;
  const sequenceMonth = record.sequence_month || record.sequenceMonth || null;

  if (orderDate) {
    const d = dayjs(orderDate);
    if (d.isValid()) return d.format("DD/MM/YY");
  }

  if (sequenceMonth) {
    const m = dayjs(`${String(sequenceMonth).slice(0, 7)}-01`);
    if (m.isValid()) return m.format("MMM YYYY");
    return String(sequenceMonth);
  }

  return "-";
};

const computeDisplaySequence = (record, allOrders) => {
  if (!record) return "-";
  if (!record.sequence_no || !record.sequence_month) {
    return String(record.id ?? "-");
  }

  const sameMonthOrders = (allOrders || [])
    .filter(
      (o) =>
        o &&
        o.sequence_month === record.sequence_month &&
        Number.isFinite(Number(o.sequence_no))
    )
    .slice();

  if (!sameMonthOrders.length) {
    return String(record.sequence_no);
  }

  sameMonthOrders.sort((a, b) => Number(b.sequence_no) - Number(a.sequence_no));
  const maxSeq = Number(sameMonthOrders[0].sequence_no) || Number(record.sequence_no) || 0;

  let indexInMonth = sameMonthOrders.findIndex((o) => o.id === record.id);

  if (indexInMonth === -1 && Number.isFinite(Number(record.sequence_no))) {
    indexInMonth = sameMonthOrders.findIndex(
      (o) => Number(o.sequence_no) === Number(record.sequence_no)
    );
  }

  if (indexInMonth === -1) {
    return String(record.sequence_no);
  }

  const displayNo = maxSeq - indexInMonth;
  return String(displayNo);
};

  const totals = useMemo(() => {
    let subtotal = 0;
    let tax = 0;
    (invoice.lines || []).forEach((l) => {
      const lt = Number(l.qty || 0) * Number(l.unitPrice || 0);
      subtotal += lt;
      tax += (lt * Number(l.taxRate || 0)) / 100;
    });
    return {
      subtotal: Number(subtotal.toFixed(2)),
      tax: Number(tax.toFixed(2)),
      grand: Number((subtotal + tax).toFixed(2)),
    };
  }, [invoice.lines]);

  const applyHeaderVatToAllLines = (percent) => {
    setInvoice((p) => ({
      ...p,
      lines: (p.lines || []).map((ln) => ({ ...ln, taxRate: Number(percent || 0) })),
    }));
  };

  const makeSnapshot = (invObj, vatPercent) => {
    const normalized = {
      id: invObj?.id || null,
      invoiceNo: invObj?.invoiceNo || null,
      status: String(invObj?.status || "draft").toLowerCase(),
      issueDate: invObj?.issueDate ? dayjs(invObj.issueDate).format("YYYY-MM-DD") : null,
      dueDate: invObj?.dueDate ? dayjs(invObj.dueDate).format("YYYY-MM-DD") : null,
      reference: invObj?.reference || "",
      customerId: invObj?.customerId || null,
      customer: {
        name: invObj?.customer?.name || "",
        address: invObj?.customer?.address || "",
        vat: invObj?.customer?.vat || "",
        email: invObj?.customer?.email || "",
      },
      note: invObj?.note || "",
      headerVatPercent: Number(vatPercent || 0),
      lines: (invObj?.lines || []).map((l) => ({
        type: normalizeLineTypeForFrontend(l.type),
        refId: l.refId || null,
        description: l.description || "",
        qty: Number(l.qty || 0),
        unitPrice: Number(l.unitPrice || 0),
        taxRate: Number(l.taxRate || 0),
        sequence_no: l.sequence_no ?? null,
        sequence_month: l.sequence_month ?? null,
        sku: l.sku || "",
      })),
    };

    return JSON.stringify(normalized);
  };

  const hasChanges = useMemo(() => {
    if (!isExistingInvoice || !initialSnapshot) return false;
    return makeSnapshot(invoice, headerVatPercent) !== initialSnapshot;
  }, [invoice, headerVatPercent, initialSnapshot, isExistingInvoice]);

  const loadContacts = async () => {
    try {
      const { data } = await axiosInstance.get("/admin/invoices/contacts");
      const list = Array.isArray(data?.contacts) ? data.contacts : Array.isArray(data) ? data : [];
      setContacts(
        list.map((c) => ({
          id: c.id,
          name: c.name || "",
          address: c.address || "",
          vat: c.vat || "",
          email: c.email || "",
        }))
      );
    } catch {
      setContacts([]);
    }
  };

  const loadNextNumberPreview = async () => {
    try {
      const { data } = await axiosInstance.get("/admin/invoices/next-number");
      setPreviewInvoiceNo(normalizePreviewInvoice(data?.nextInvoiceNo || data?.next || ""));
    } catch {
      setPreviewInvoiceNo("");
    }
  };

  const loadSellerInfo = async () => {
    try {
      const { data } = await axiosInstance.get("/admin/invoices/seller");
      const s = data?.seller || data || {};
      const address = (s.address || s.addressText || "")
        .toString()
        .replace(/\r/g, "")
        .split("\n")
        .filter(Boolean);

      setSeller({
        name: s.name || s.companyName || "",
        addressLines: address,
        email: s.email || "",
        phone: s.phone || "",
        vat: s.vat || s.vatNumber || "",
      });
    } catch {
      const lsName = localStorage.getItem("companyName") || "";
      const lsAddr = (localStorage.getItem("companyAddress") || "").split("\n").filter(Boolean);
      const lsVat = localStorage.getItem("companyVat") || "";
      const lsEmail = localStorage.getItem("companyEmail") || "";
      const lsPhone = localStorage.getItem("companyPhone") || "";

      setSeller({
        name: lsName,
        addressLines: lsAddr,
        email: lsEmail,
        phone: lsPhone,
        vat: lsVat,
      });
    }
  };

  const loadProductionLookup = async () => {
    try {
      const token = localStorage.getItem("token");
      const { data } = await axiosInstance.get("/admin/production/list", {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const list = Array.isArray(data?.orders) ? data.orders : Array.isArray(data) ? data : [];
      const map = {};
      list.forEach((o) => {
        const id = o.id;
        map[String(id)] = {
          sequence_no: o.sequence_no ?? o.sequenceNo ?? null,
          sequence_month: o.sequence_month ?? o.sequenceMonth ?? null,
        };
      });
      setProdLookup(map);
    } catch {
      setProdLookup({});
    }
  };

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        await Promise.all([loadContacts(), loadSellerInfo(), loadProductionLookup()]);

        if (!invoiceId) {
          await loadNextNumberPreview();
          setInitialSnapshot("");
        }

        let parts = [];
        try {
          const { data: partsRes } = await axiosInstance.get("/admin/invoices/inventory-parts");
          parts = Array.isArray(partsRes?.parts) ? partsRes.parts : [];
        } catch {
          try {
            const { data: partsRes2 } = await axiosInstance.get("/admin/inventory-parts");
            parts = Array.isArray(partsRes2?.parts) ? partsRes2.parts : [];
          } catch {}
        }
        setInventoryParts(parts);

        if (invoiceId) {
          const { data } = await axiosInstance.get(`/admin/invoices/${invoiceId}`);
          const inv = data?.invoice || data || {};

          const customer = {
            name: inv.customerName || inv.contact?.name || "",
            address: inv.billTo || inv.contact?.address || "",
            vat: inv.customerVat || inv.contact?.vat || "",
            email: inv.contact?.email || inv.contactEmail || "",
          };

          const contactId = inv.contactId || inv.contact?.id || null;

          let derivedVat = 0;
          if (Array.isArray(inv.items) && inv.items.length) {
            const first = Number(inv.items[0]?.taxRate || 0);
            const allSame = inv.items.every((it) => Number(it.taxRate || 0) === first);
            derivedVat = allSame ? first : 0;
          }

          const loadedInvoice = {
            id: inv.id ?? null,
            invoiceNo: inv.invoiceNo ?? null,
            status: String(inv.status || "draft").toLowerCase(),
            issueDate: inv.issueDate ? dayjs(inv.issueDate) : dayjs(),
            dueDate: inv.dueDate ? dayjs(inv.dueDate) : null,
            reference: inv.reference || "",
            customerId: contactId,
            customer,
            lines:
              inv.lines ||
              inv.items?.map((it) => ({
                type: normalizeLineTypeForFrontend(it.lineType),
                refId: it.refId || null,
                description: it.description || "",
                qty: Number(it.quantity || it.qty || 1),
                unitPrice: Number(it.unitPrice || it.price || 0),
                taxRate: Number(it.taxRate || 0),
                sequence_no: it.sequence_no ?? it.sequenceNo ?? null,
                sequence_month: it.sequence_month ?? it.sequenceMonth ?? null,
                sku: it.sku || "",
              })) ||
              [],
            note: inv.note || inv.notes || "",
          };

          setInvoice(loadedInvoice);
          setHeaderVatPercent(derivedVat);
          setInitialSnapshot(makeSnapshot(loadedInvoice, derivedVat));
        } else {
          let prevLines = [];
          try {
            const rawPrev = sessionStorage.getItem("invoice:existingLines");
            if (rawPrev) {
              prevLines = JSON.parse(rawPrev);
              sessionStorage.removeItem("invoice:existingLines");
            }
          } catch {}

          let incomingLines = [];
          let suggestedRef = "";

          if (selectedAtStart?.length) {
            try {
              const { data } = await axiosInstance.get("/admin/invoices/production-lines", {
                params: { ids: selectedAtStart.join(",") },
              });
              if (Array.isArray(data?.lines)) incomingLines = data.lines;
              suggestedRef = (data?.reference || "").trim() || `ORD-${selectedAtStart[0]}`;
            } catch {}

            if (!incomingLines.length) {
              try {
                const raw = sessionStorage.getItem("invoice:selectedLinesPayload");
                if (raw) incomingLines = JSON.parse(raw) || [];
              } catch {}
            }

            try {
              sessionStorage.removeItem("invoice:selectedLinesPayload");
            } catch {}
          }

          const seen = new Set();
          const merged = [...prevLines, ...incomingLines]
            .filter((ln) => {
              const key = `${ln?.type || ""}:${ln?.refId || ""}`;
              if (seen.has(key)) return false;
              seen.add(key);
              return true;
            })
            .map((ln) => ({
              ...ln,
              type: normalizeLineTypeForFrontend(ln.type),
              taxRate: Number(headerVatPercent || 0),
            }));

          setInvoice((p) => ({
            ...p,
            lines: merged.length ? merged : p.lines,
            reference: p.reference?.trim() ? p.reference : suggestedRef || p.reference || "",
          }));
        }
      } catch (e) {
        message.error(e?.response?.data?.message || "Failed to load invoice data");
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [invoiceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSelectContact = (id) => {
    const c = contacts.find((x) => Number(x.id) === Number(id));
    setInvoice((p) => ({
      ...p,
      customerId: id,
      customer: c
        ? {
            name: c.name || "",
            address: c.address || "",
            vat: c.vat || "",
            email: c.email || "",
          }
        : { name: "", address: "", vat: "", email: "" },
    }));
  };

  const openAddContact = () => {
    contactForm.resetFields();
    setContactModalOpen(true);
  };

  const saveContact = async () => {
    try {
      const vals = await contactForm.validateFields();
      const { data } = await axiosInstance.post("/admin/invoices/contacts", {
        name: vals.name?.trim(),
        address: vals.address?.trim() || "",
        email: vals.email?.trim() || "",
        vat: vals.vat?.trim() || "",
      });

      const created = data?.contact || data;
      await loadContacts();

      setInvoice((p) => ({
        ...p,
        customerId: created.id,
        customer: {
          name: created.name || "",
          address: created.address || "",
          vat: created.vat || "",
          email: created.email || "",
        },
      }));

      setContactModalOpen(false);
      message.success("Contact saved");
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to save contact");
    }
  };

  const addManualRow = () => {
    setInvoice((p) => ({
      ...p,
      lines: [
        ...(p.lines || []),
        {
          type: "manual",
          refId: null,
          description: "",
          qty: 1,
          unitPrice: 0,
          taxRate: Number(headerVatPercent || 0),
        },
      ],
    }));
  };

  const changeLine = (idx, patch) => {
    setInvoice((p) => {
      const lines = [...p.lines];
      lines[idx] = { ...lines[idx], ...patch };
      return { ...p, lines };
    });
  };

  const removeLine = (idx) =>
    setInvoice((p) => ({ ...p, lines: p.lines.filter((_, i) => i !== idx) }));

  const setLineFromInventory = (idx, partId) => {
    const part = inventoryParts.find((x) => Number(x.id) === Number(partId));
    if (!part) return;
    changeLine(idx, {
      type: "part",
      refId: part.id,
      description: part.product_name,
      unitPrice: Number(part.factory_price ?? part.price_euro ?? 0),
      taxRate: Number(headerVatPercent || 0),
    });
  };

  const addInventoryPart = () => {
    const part = inventoryParts.find((x) => x.id === addingPart.productId);
    if (!part) return message.warning("Select a part first");

    setInvoice((p) => ({
      ...p,
      lines: [
        ...p.lines,
        {
          type: "part",
          refId: part.id,
          description: part.product_name,
          qty: Number(addingPart.qty || 1),
          unitPrice: Number(addingPart.price || part.factory_price || part.price_euro || 0),
          taxRate: Number(headerVatPercent || 0),
        },
      ],
    }));

    setAddingPart({ productId: null, qty: 1, price: 0 });
    setPartModalOpen(false);
  };

 const openProductionPicker = async () => {
  setProdModalOpen(true);
  setProdSelectedRowKeys([]);
  setProdLoading(true);

  try {
    const token = localStorage.getItem("token");

    const [{ data }, { data: invoicedData }] = await Promise.all([
      axiosInstance.get("/admin/production/list", {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }),
      axiosInstance.get("/admin/invoices/invoiced-production-ids"),
    ]);

    const list = Array.isArray(data?.orders) ? data.orders : Array.isArray(data) ? data : [];

    const alreadyInvoicedIds = new Set(
      (Array.isArray(invoicedData?.ids) ? invoicedData.ids : [])
        .map((x) => Number(x))
        .filter((x) => Number.isInteger(x) && x > 0)
    );

    const orderIds = list
      .map((o) => Number(o.orderId || o.order_id || o.order?.id || o.OrderId || 0))
      .filter((n) => Number.isInteger(n) && n > 0);

    let priceMap = {};
    if (orderIds.length) {
      try {
        const { data: priceData } = await axiosInstance.get("/admin/invoices/production-lines", {
          params: { ids: orderIds.join(",") },
        });

        const lines = Array.isArray(priceData?.lines) ? priceData.lines : [];
        lines.forEach((ln) => {
          const key = Number(ln.refId || 0);
          if (key > 0) {
            priceMap[key] = {
              unitPrice: Number(ln.unitPrice ?? ln.unit_price ?? ln.price ?? 0),
              qty: Number(ln.qty ?? ln.quantity ?? 1),
            };
          }
        });
      } catch {
        priceMap = {};
      }
    }

   // In openProductionPicker(), keep the normalized row mapping like this
const normalized = list
  .map((o) => {
    const productionId = Number(o.id || 0);
    const orderId = Number(o.orderId || o.order_id || o.order?.id || o.OrderId || 0);

    const stableId = orderId > 0 ? orderId : productionId;
    if (!Number.isInteger(stableId) || stableId <= 0) return null;

    const qty = Number(o.quantity || o.qty || 1);

    const fallbackPrice =
      o.factory_price !== undefined && o.factory_price !== null
        ? Number(o.factory_price)
        : o.factoryPrice !== undefined && o.factoryPrice !== null
        ? Number(o.factoryPrice)
        : null;

    const priceVal =
      orderId > 0 && priceMap[orderId]?.unitPrice !== undefined
        ? Number(priceMap[orderId].unitPrice)
        : fallbackPrice !== null
        ? Number(fallbackPrice)
        : null;

    const alreadyInvoiced =
      alreadyInvoicedIds.has(stableId) ||
      (orderId > 0 && alreadyInvoicedIds.has(orderId)) ||
      (productionId > 0 && alreadyInvoicedIds.has(productionId));

    return {
      id: stableId,
      productionId,
      orderId: orderId || null,
      size: typeof o.size === "string" ? o.size : (o.size && o.size.size) || "N/A",
      product_name: o.product_name || o.productName || o.product || "",
      color: o.color || o.fabric_color_name || "",
      storageType: o.storageType || o.storage_type || "",
      orderDate: o.orderDate || o.order_date || o.createdAt || null,
      quantity: qty,
      price: priceVal,
      lineTotal: priceVal !== null ? Number((priceVal * qty).toFixed(2)) : null,
      sequence_no: o.sequence_no ?? o.sequenceNo ?? null,
      sequence_month: o.sequence_month ?? o.sequenceMonth ?? null,
      factory_price: o.factory_price ?? o.factoryPrice ?? null,
      alreadyInvoiced,
    };
  })
  .filter(Boolean);

    setProdOrders(normalized);

    const map = {};
    normalized.forEach((r) => {
      map[String(r.id)] = {
        sequence_no: r.sequence_no ?? null,
        sequence_month: r.sequence_month ?? null,
      };
    });
    setProdLookup((prev) => ({ ...prev, ...map }));

    const existingIdsArr = (invoice.lines || [])
      .filter((ln) => ln.type === "production" && ln.refId != null)
      .map((ln) => Number(ln.refId))
      .filter((n) => Number.isInteger(n) && n > 0);

    setProdExistingIds(existingIdsArr);

    const existingSet = new Set(existingIdsArr);
    setProdSelectedRowKeys(
      normalized.filter((r) => existingSet.has(Number(r.id))).map((r) => r.id)
    );
  } catch (e) {
    message.error(e?.response?.data?.message || "Failed to load production orders");
    setProdOrders([]);
    setProdExistingIds([]);
    setProdSelectedRowKeys([]);
  } finally {
    setProdLoading(false);
  }
};

 const addSelectedProductionToInvoice = async () => {
  const newProdIds = prodSelectedRowKeys.filter((id) => !prodExistingSet.has(Number(id)));

  if (!newProdIds.length) {
    setProdModalOpen(false);
    return;
  }

  const selectableRows = newProdIds
    .map((id) => prodOrders.find((r) => Number(r.id) === Number(id)))
    .filter(Boolean)
    .filter((r) => !r.alreadyInvoiced);

  if (!selectableRows.length) {
    message.warning("Selected production orders are already invoiced.");
    return;
  }

  const orderIds = selectableRows
    .map((r) => Number(r.orderId || r.id || 0))
    .filter((n) => Number.isInteger(n) && n > 0);

  try {
    const { data } = await axiosInstance.get("/admin/invoices/production-lines", {
      params: { ids: orderIds.join(",") },
    });

    const blockedIds = Array.isArray(data?.blockedIds)
      ? data.blockedIds.map((x) => Number(x))
      : [];

    if (blockedIds.length) {
      message.warning("Some selected production orders are already invoiced and were skipped.");
    }

    const newLines = (data.lines || []).map((ln) => ({
      ...ln,
      type: normalizeLineTypeForFrontend(ln.type),
      taxRate: Number(headerVatPercent || 0),
    }));

    setInvoice((p) => {
      const existing = new Set((p.lines || []).map((x) => `${x.type}:${x.refId}`));
      const toAdd = newLines.filter((ln) => {
        const key = `${ln.type || "production"}:${ln.refId ?? ""}`;
        if (existing.has(key)) return false;
        existing.add(key);
        return true;
      });
      return { ...p, lines: [...p.lines, ...toAdd] };
    });

    setProdModalOpen(false);
  } catch (e) {
    message.error(e?.response?.data?.message || "Failed to load production prices");
  }
};

  const buildPayload = (statusOverride) => ({
    contactId: invoice.customerId || null,
    customerName: invoice.customer?.name || "",
    billTo: invoice.customer?.address || "",
    shipTo: "",
    issueDate: invoice.issueDate?.format("YYYY-MM-DD") || null,
    dueDate: invoice.dueDate?.format("YYYY-MM-DD") || null,
    reference: invoice.reference,
    notes: invoice.note,
    customerVat: invoice.customer?.vat || "",
    status: statusOverride || invoice.status || "draft",
    items: (invoice.lines || []).map((l) => ({
      lineType: normalizeLineTypeForPayload(l.type),
      refId: l.refId || null,
      description: l.description,
      quantity: Number(l.qty || 1),
      unitPrice: Number(l.unitPrice || 0),
      taxRate: Number(l.taxRate || 0),
      sequence_no: l.sequence_no ?? null,
      sequence_month: l.sequence_month ?? null,
    })),
  });

  const persistInvoice = async (statusOverride) => {
    const payload = buildPayload(statusOverride);

    let data;
    if (!invoice.id) {
      ({ data } = await axiosInstance.post("/admin/invoices", payload));
    } else {
      ({ data } = await axiosInstance.put(`/admin/invoices/${invoice.id}`, payload));
    }

    const inv = data?.invoice || data || {};

    const normalized = {
      id: inv.id ?? invoice.id,
      invoiceNo: inv.invoiceNo || invoice.invoiceNo || null,
      status: String(inv.status || statusOverride || invoice.status || "draft").toLowerCase(),
      issueDate: inv.issueDate ? dayjs(inv.issueDate) : invoice.issueDate,
      dueDate: inv.dueDate ? dayjs(inv.dueDate) : invoice.dueDate,
      reference: inv.reference || invoice.reference || "",
      customerId: inv.contactId || inv.contact?.id || invoice.customerId || null,
      customer: {
        name: inv.customerName || inv.contact?.name || invoice.customer?.name || "",
        address: inv.billTo || inv.contact?.address || invoice.customer?.address || "",
        vat: inv.customerVat || inv.contact?.vat || invoice.customer?.vat || "",
        email: inv.contact?.email || inv.contactEmail || invoice.customer?.email || "",
      },
      lines:
        inv.items?.map((it) => ({
          type: normalizeLineTypeForFrontend(it.lineType),
          refId: it.refId || null,
          description: it.description || "",
          qty: Number(it.quantity || 1),
          unitPrice: Number(it.unitPrice || 0),
          taxRate: Number(it.taxRate || 0),
          sequence_no: it.sequence_no ?? it.sequenceNo ?? null,
          sequence_month: it.sequence_month ?? it.sequenceMonth ?? null,
          sku: it.sku || "",
        })) || invoice.lines,
      note: inv.notes || inv.note || invoice.note || "",
    };

    setInvoice(normalized);

    if (!invoice.id && normalized.id) {
      navigate(`/invoices/${normalized.id}`, { replace: true });
    }

    setInitialSnapshot(makeSnapshot(normalized, headerVatPercent));

    return normalized;
  };

  const blobToBase64 = (blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const printBlob = (blob) => {
    const url = URL.createObjectURL(blob);
    const win = window.open(url, "_blank");
    if (win) {
      win.onload = () => {
        win.print();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      };
    }
  };

  const saveDraft = async () => {
    setActionLoading("saveDraft");
    try {
      await persistInvoice("draft");
      message.success("Draft saved");
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to save draft");
    } finally {
      setActionLoading("");
    }
  };

  const createInvoice = async () => {
    setActionLoading("createInvoice");
    try {
      const inv = await persistInvoice("sent");
      message.success(`Invoice created: ${inv.invoiceNo || "INV-###"}`);
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to create invoice");
    } finally {
      setActionLoading("");
    }
  };

  const createAndEmail = async () => {
    setActionLoading("createAndEmail");
    try {
      const inv = await persistInvoice("sent");
      const { doc, displayInv } = buildInvoicePdfDoc(inv, {
  forceProductionRefId: true,
});
      const pdfBlob = doc.output("blob");
      const pdfBase64 = await blobToBase64(pdfBlob);

      await axiosInstance.post(`/admin/invoices/${inv.id}/email`, {
        pdfBase64,
      });

      message.success(`Invoice ${displayInv} created and emailed`);
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to create and email invoice");
    } finally {
      setActionLoading("");
    }
  };

  const editChanges = async () => {
    if (!invoice.id) return;
    setActionLoading("editChanges");
    try {
      await persistInvoice(invoice.status || "sent");
      message.success("Invoice updated");
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to update invoice");
    } finally {
      setActionLoading("");
    }
  };

  const emailInvoice = async () => {
    if (!invoice.id) {
      message.warning("Please create the invoice first");
      return;
    }

    setActionLoading("emailInvoice");
    try {
      let currentInvoice = invoice;

      if (hasChanges) {
        currentInvoice = await persistInvoice(invoice.status || "sent");
      }

      const { doc } = buildInvoicePdfDoc(currentInvoice, {
  forceProductionRefId: true,
});
      const pdfBlob = doc.output("blob");
      const pdfBase64 = await blobToBase64(pdfBlob);

      await axiosInstance.post(`/admin/invoices/${currentInvoice.id}/email`, {
        pdfBase64,
      });

      message.success("Invoice emailed");
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to email invoice");
    } finally {
      setActionLoading("");
    }
  };

  const buildInvoicePdfDoc = (sourceInvoice = invoice, options = {}) => {
  const forceProductionRefId = options.forceProductionRefId === true;
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    doc.setProperties({ title: `Invoice ${sourceInvoice.invoiceNo || ""}`, subject: "Invoice" });

    const pageH = doc.internal.pageSize.getHeight();
    const M = 60;
    const G = 18;
    const contentW = doc.internal.pageSize.getWidth() - M * 2;

    const displayInv = sourceInvoice.invoiceNo || previewInvoiceNo || "INV-###";

    const localTotals = (sourceInvoice.lines || []).reduce(
      (acc, l) => {
        const lt = Number(l.qty || 0) * Number(l.unitPrice || 0);
        acc.subtotal += lt;
        acc.tax += (lt * Number(l.taxRate || 0)) / 100;
        return acc;
      },
      { subtotal: 0, tax: 0 }
    );
    localTotals.subtotal = Number(localTotals.subtotal.toFixed(2));
    localTotals.tax = Number(localTotals.tax.toFixed(2));
    localTotals.grand = Number((localTotals.subtotal + localTotals.tax).toFixed(2));

    doc.setFont("helvetica", "bold");
    doc.setFontSize(28);
    doc.text("INVOICE", M, 80);

    const leftW = Math.round(contentW * 0.42);
    const metaW = Math.round(contentW * 0.25);
    const rightW = contentW - leftW - metaW - G * 2;

    const leftX = M;
    const metaX = leftX + leftW + G;
    const rightX = metaX + metaW + G;
    const headerY = 112;

    const boxH = 150;
    doc.setLineWidth(1);
    doc.rect(leftX, headerY, leftW, boxH);
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("Deliver to", leftX + 12, headerY + 18);
    doc.setFont("helvetica", "normal");

    const deliverLinesRaw = [
      sourceInvoice.customer?.name || "",
      sourceInvoice.customer?.address || "",
      sourceInvoice.customer?.vat ? `VAT: ${sourceInvoice.customer.vat}` : "",
    ]
      .filter(Boolean)
      .join("\n");

    const deliverLines = doc.splitTextToSize(deliverLinesRaw, leftW - 24);
    let dy = headerY + 40;
    deliverLines.forEach((l) => {
      doc.text(l, leftX + 12, dy);
      dy += 16;
    });

    let y = headerY;
    doc.setFont("helvetica", "bold");
    doc.text("Invoice Date", rightX, y + 10);
    doc.setFont("helvetica", "normal");
    doc.text(
      sourceInvoice.issueDate ? dayjs(sourceInvoice.issueDate).format("DD MMM YYYY") : "-",
      rightX,
      y + 28
    );

    doc.setFont("helvetica", "bold");
    doc.text("Due Date", rightX, y + 52);
    doc.setFont("helvetica", "normal");
    doc.text(
      sourceInvoice.dueDate ? dayjs(sourceInvoice.dueDate).format("DD MMM YYYY") : "-",
      rightX,
      y + 70
    );

    doc.setFont("helvetica", "bold");
    doc.text("Invoice Number", rightX, y + 94);
    doc.setFont("helvetica", "normal");
    doc.text(displayInv, rightX, y + 112);

    if (sourceInvoice.reference?.trim()) {
      doc.setFont("helvetica", "bold");
      doc.text("Reference", rightX, y + 136);
      doc.setFont("helvetica", "normal");
      const refLines = doc.splitTextToSize(sourceInvoice.reference, rightW);
      doc.text(refLines, rightX, y + 154);
    }

    y = headerY;
    doc.setFont("helvetica", "bold");
    const sellerName = doc.splitTextToSize(seller.name || "", metaW);
    if (sellerName.length) doc.text(sellerName, metaX, y + 10);
    doc.setFont("helvetica", "normal");
    let sy = y + (sellerName.length ? 28 : 10);

    const sellerAddr = doc.splitTextToSize((seller.addressLines || []).join("\n"), metaW);
    sellerAddr.forEach((l) => {
      doc.text(l, metaX, sy);
      sy += 16;
    });

    if (seller.email) {
      doc.text(`Email: ${seller.email}`, metaX, sy);
      sy += 16;
    }
    if (seller.phone) {
      doc.text(`Phone: ${seller.phone}`, metaX, sy);
      sy += 16;
    }
    if (seller.vat) {
      doc.setFont("helvetica", "bold");
      doc.text("VAT Number", metaX, sy + 8);
      doc.setFont("helvetica", "normal");
      doc.text(String(seller.vat), metaX, sy + 24);
    }

    autoTable(doc, {
      startY: headerY + boxH + 30,
      head: [["Item", "Description", "Qty", "Unit Price", "Line Total"]],
      body: (sourceInvoice.lines || []).map((ln) => {
       const code = (() => {
  if (ln.type === "production") {
    if (forceProductionRefId) {
      return String(ln.refId || "");
    }

    if (ln.sku) return ln.sku;
    if (ln.sequence_no) return String(ln.sequence_no);

    const lookup = prodLookup[String(ln.refId)];
    if (lookup && (lookup.sequence_no ?? null) != null) {
      return String(lookup.sequence_no);
    }

    return String(ln.refId || "");
  }

  if (ln.sku) return ln.sku;
  return String(ln.refId ?? "");
})();

        const qty = Number(ln.qty || 0);
        const price = Number(ln.unitPrice || 0);
        const lineTotal = qty * price;

        return [
          code,
          ln.description || "",
          qty.toFixed(2),
          formatCurrency(price),
          formatCurrency(lineTotal),
        ];
      }),
      margin: { left: M, right: M },
      styles: { fontSize: 9, cellPadding: 6, overflow: "linebreak" },
      headStyles: { fontStyle: "bold" },
    });

    const y0 = doc.lastAutoTable.finalY + 18;
    const boxW2 = 260;
    const boxX = M + contentW - boxW2;

    doc.setLineWidth(1);
    doc.rect(boxX, y0, boxW2, 104);
    doc.setFont("helvetica", "normal");
    doc.text("Subtotal", boxX + 12, y0 + 24);
    doc.text("Total VAT", boxX + 12, y0 + 44);
    doc.setFont("helvetica", "bold");
    doc.text("Total", boxX + 12, y0 + 74);

    doc.setFont("helvetica", "normal");
    doc.text(formatCurrency(localTotals.subtotal), boxX + boxW2 - 12, y0 + 24, {
      align: "right",
    });
    doc.text(formatCurrency(localTotals.tax), boxX + boxW2 - 12, y0 + 44, {
      align: "right",
    });
    doc.setFont("helvetica", "bold");
    doc.text(formatCurrency(localTotals.grand), boxX + boxW2 - 12, y0 + 74, {
      align: "right",
    });

    const vatPolicy = headerVatPercent
      ? `Amounts include VAT at ${headerVatPercent}% where applicable.`
      : "No VAT applied.";
    doc.setFont("helvetica", "normal");
    const noteLines = doc.splitTextToSize(vatPolicy, contentW - 280);
    doc.text(noteLines, M, y0 + 16);

    if (sourceInvoice.note?.trim()) {
      const ny = y0 + 104 + 16;
      const nl = doc.splitTextToSize(sourceInvoice.note.trim(), contentW);
      let nyPos = ny;
      nl.forEach((line) => {
        if (nyPos > pageH - M) {
          doc.addPage();
          nyPos = M;
        }
        doc.text(line, M, nyPos);
        nyPos += 16;
      });
    }

    return { doc, displayInv };
  };

  const buildDeliveryNotePdfDoc = (sourceInvoice = invoice) => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const displayInv = sourceInvoice.invoiceNo || previewInvoiceNo || "INV-###";
    const M = 50;

    doc.setProperties({
      title: `Delivery Note ${displayInv}`,
      subject: "Delivery Note",
    });

    doc.setFont("helvetica", "bold");
    doc.setFontSize(26);
    doc.text("DELIVERY NOTE", M, 70);

    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");

    if (seller.name) doc.text(seller.name, M, 100);
    let sellerY = 118;
    (seller.addressLines || []).forEach((line) => {
      doc.text(line, M, sellerY);
      sellerY += 14;
    });
    if (seller.email) {
      doc.text(`Email: ${seller.email}`, M, sellerY);
      sellerY += 14;
    }
    if (seller.phone) {
      doc.text(`Phone: ${seller.phone}`, M, sellerY);
    }

    const rightX = doc.internal.pageSize.getWidth() - M;
    doc.setFont("helvetica", "bold");
    doc.text("Date", rightX - 120, 100);
    doc.setFont("helvetica", "normal");
    doc.text(
      sourceInvoice.issueDate ? dayjs(sourceInvoice.issueDate).format("DD MMM YYYY") : "-",
      rightX - 120,
      118
    );

    doc.setFont("helvetica", "bold");
    doc.text("Invoice Number", rightX - 120, 142);
    doc.setFont("helvetica", "normal");
    doc.text(displayInv, rightX - 120, 160);

    if (sourceInvoice.reference?.trim()) {
      doc.setFont("helvetica", "bold");
      doc.text("Reference", rightX - 120, 184);
      doc.setFont("helvetica", "normal");
      doc.text(sourceInvoice.reference, rightX - 120, 202);
    }

    autoTable(doc, {
      startY: 230,
      head: [["Deliver To"]],
      body: [
        [
          [sourceInvoice.customer?.name || "", sourceInvoice.customer?.address || ""]
            .filter(Boolean)
            .join("\n"),
        ],
      ],
      margin: { left: M, right: M },
      styles: { fontSize: 10, cellPadding: 8, overflow: "linebreak" },
      headStyles: { fontStyle: "bold" },
    });

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 18,
      head: [["Item", "Description", "Qty"]],
      body: (sourceInvoice.lines || []).map((ln) => {
        const code = (() => {
          if (ln.sku) return ln.sku;
          if (ln.type === "production") {
            if (ln.sequence_no) return String(ln.sequence_no);
            const lookup = prodLookup[String(ln.refId)];
            if (lookup && (lookup.sequence_no ?? null) != null) return String(lookup.sequence_no);
            return String(ln.refId || "");
          }
          return String(ln.refId ?? "");
        })();

        return [code, ln.description || "", String(Number(ln.qty || 0).toFixed(2))];
      }),
      margin: { left: M, right: M },
      styles: { fontSize: 10, cellPadding: 8, overflow: "linebreak" },
      headStyles: { fontStyle: "bold" },
    });

    return { doc, displayInv };
  };

  const downloadInvoicePdf = () => {
    const { doc, displayInv } = buildInvoicePdfDoc(invoice);
    downloadBlob(doc.output("blob"), `${displayInv}.pdf`);
  };

  const printInvoicePdf = () => {
    const { doc } = buildInvoicePdfDoc(invoice);
    printBlob(doc.output("blob"));
  };

  const downloadDeliveryNotePdf = () => {
    const { doc, displayInv } = buildDeliveryNotePdfDoc(invoice);
    downloadBlob(doc.output("blob"), `${displayInv}-delivery-note.pdf`);
  };

  const printDeliveryNotePdf = () => {
    const { doc } = buildDeliveryNotePdfDoc(invoice);
    printBlob(doc.output("blob"));
  };

  const columns = [
    {
      title: "Item",
      dataIndex: "description",
      key: "desc",
      render: (v, r, idx) => {
        const selectionValue =
          r.type === "part" && r.refId != null ? `inv_${r.refId}` : "__manual__";

        return (
          <div className="flex gap-2">
            <Select
              value={selectionValue}
              showSearch
              optionFilterProp="children"
              style={{ minWidth: 240 }}
              onChange={(val) => {
                if (val === "__manual__") {
                  changeLine(idx, { type: "manual", refId: null });
                } else if (String(val).startsWith("inv_")) {
                  const id = Number(String(val).split("_")[1]);
                  setLineFromInventory(idx, id);
                }
              }}
            >
              <Option value="__manual__">Manual entry</Option>
              {inventoryParts.map((p) => (
                <Option key={p.id} value={`inv_${p.id}`}>
                  {p.product_name}
                </Option>
              ))}
            </Select>

            <Input
              placeholder="Description"
              value={r.description}
              onChange={(e) => changeLine(idx, { description: e.target.value })}
            />
          </div>
        );
      },
    },
    {
      title: "Qty",
      dataIndex: "qty",
      key: "qty",
      width: 90,
      render: (v, r, idx) => (
        <InputNumber
          min={1}
          value={v}
          onChange={(n) => changeLine(idx, { qty: n })}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Price",
      dataIndex: "unitPrice",
      key: "price",
      width: 140,
      render: (v, r, idx) => (
        <InputNumber
          min={0}
          value={v}
          onChange={(n) => changeLine(idx, { unitPrice: n })}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Tax rate",
      dataIndex: "taxRate",
      key: "taxRate",
      width: 120,
      render: (v, r, idx) => (
        <InputNumber
          min={0}
          max={100}
          value={Number(v || 0)}
          formatter={(val) => `${val}%`}
          parser={(val) => Number(String(val).replace("%", ""))}
          onChange={(n) => changeLine(idx, { taxRate: Number(n || 0) })}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Tax amount",
      key: "taxAmt",
      width: 140,
      render: (_, r) => {
        const lt = Number(r.qty || 0) * Number(r.unitPrice || 0);
        const tax = (lt * Number(r.taxRate || 0)) / 100;
        return <b>{formatCurrency(tax)}</b>;
      },
    },
    {
      title: "Amount",
      key: "total",
      width: 140,
      render: (_, r) => {
        const lt = Number(r.qty || 0) * Number(r.unitPrice || 0);
        const tax = (lt * Number(r.taxRate || 0)) / 100;
        return <b>{formatCurrency(lt + tax)}</b>;
      },
    },
    {
      title: "",
      key: "a",
      width: 64,
      render: (_, __, idx) => (
        <Button danger icon={<DeleteOutlined />} onClick={() => removeLine(idx)} />
      ),
    },
  ];

  const downloadMenu = {
    items: [
      { key: "download-invoice", label: "Download Invoice" },
      { key: "download-delivery-note", label: "Download Delivery Note" },
    ],
    onClick: ({ key }) => {
      if (key === "download-invoice") downloadInvoicePdf();
      if (key === "download-delivery-note") downloadDeliveryNotePdf();
    },
  };

  const printMenu = {
    items: [
      { key: "print-invoice", label: "Print Invoice" },
      { key: "print-delivery-note", label: "Print Delivery Note" },
    ],
    onClick: ({ key }) => {
      if (key === "print-invoice") printInvoicePdf();
      if (key === "print-delivery-note") printDeliveryNotePdf();
    },
  };

  return (
    <Spin spinning={loading || actionLoading} size="large" className="min-h-screen">
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-6 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex items-start gap-3">
                <Button
                  icon={<ArrowLeftOutlined />}
                  onClick={() => navigate(-1)}
                  className="rounded-lg mt-1"
                />
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 mb-2">
                    {invoice.invoiceNo ? `Invoice ${invoice.invoiceNo}` : "New Invoice"}
                  </h1>
                  <p className="text-gray-600">
                    Create and manage invoice details, items, VAT and customer billing information
                  </p>
                </div>
              </div>

              <div className="flex flex-col items-end gap-3">
                <div className="flex flex-wrap gap-2">
                  {isExistingInvoice ? (
                    <>
                      <Button
                        icon={<EditOutlined />}
                        onClick={editChanges}
                        loading={actionLoading === "editChanges"}
                        disabled={!hasChanges}
                        className="rounded-lg font-medium"
                      >
                        Edit Changes
                      </Button>

                      <Button
                        icon={<MailOutlined />}
                        onClick={emailInvoice}
                        loading={actionLoading === "emailInvoice"}
                        disabled={!hasChanges}
                        type="primary"
                        className="bg-blue-500 hover:bg-blue-600 border-blue-500 text-white rounded-lg font-medium"
                      >
                        Email
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button onClick={saveDraft} loading={actionLoading === "saveDraft"} className="rounded-lg font-medium">
                        Save Draft
                      </Button>

                      <Button
                        onClick={createInvoice}
                        loading={actionLoading === "createInvoice"}
                        className="rounded-lg font-medium"
                      >
                        Create Invoice
                      </Button>

                      <Button
                        icon={<MailOutlined />}
                        onClick={createAndEmail}
                        loading={actionLoading === "createAndEmail"}
                        type="primary"
                        className="bg-blue-500 hover:bg-blue-600 border-blue-500 text-white rounded-lg font-medium"
                      >
                        Create & Email
                      </Button>
                    </>
                  )}
                </div>

                <div className="flex gap-2">
                  <Dropdown menu={downloadMenu} trigger={["click"]}>
                    <Button icon={<DownloadOutlined />} className="rounded-lg" />
                  </Dropdown>

                  <Dropdown menu={printMenu} trigger={["click"]}>
                    <Button icon={<PrinterOutlined />} className="rounded-lg" />
                  </Dropdown>
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
              <div className="mb-5">
                <h2 className="text-lg font-semibold text-gray-900 mb-1">Invoice Details</h2>
                <p className="text-sm text-gray-500">
                  Basic invoice information and customer selection
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-2">Contact</label>
                  <div className="flex gap-2">
                    <Select
                      showSearch
                      placeholder="Select contact"
                      value={invoice.customerId ?? undefined}
                      onChange={onSelectContact}
                      optionFilterProp="children"
                      style={{ flex: 1 }}
                      allowClear
                      className="invoice-input"
                    >
                      {contacts.map((c) => (
                        <Option key={c.id} value={c.id}>
                          {c.name}
                        </Option>
                      ))}
                    </Select>

                    <Tooltip title="Add Customer">
                      <Button
                        icon={<UserAddOutlined />}
                        onClick={openAddContact}
                        className="rounded-lg"
                      />
                    </Tooltip>
                  </div>
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-2">Issue Date</label>
                  <DatePicker
                    style={{ width: "100%" }}
                    value={invoice.issueDate}
                    onChange={(d) => setInvoice((p) => ({ ...p, issueDate: d }))}
                    className="invoice-input"
                  />
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-2">Due Date</label>
                  <DatePicker
                    style={{ width: "100%" }}
                    value={invoice.dueDate}
                    onChange={(d) => setInvoice((p) => ({ ...p, dueDate: d }))}
                    className="invoice-input"
                  />
                </div>

                <div className="flex flex-col md:col-span-2">
                  <label className="text-sm font-medium text-gray-700 mb-2">Reference</label>
                  <Input
                    value={invoice.reference}
                    onChange={(e) => setInvoice((p) => ({ ...p, reference: e.target.value }))}
                    className="invoice-input"
                    placeholder="Enter reference"
                  />
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-2">Invoice Number</label>
                  <Input
                    value={invoice.invoiceNo || previewInvoiceNo || "—"}
                    disabled
                    className="invoice-input"
                  />
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-2">Bill To (Name)</label>
                  <Input
                    value={invoice.customer?.name || ""}
                    onChange={(e) =>
                      setInvoice((p) => ({
                        ...p,
                        customer: { ...p.customer, name: e.target.value },
                      }))
                    }
                    className="invoice-input"
                    placeholder="Customer name"
                  />
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-2">Customer VAT</label>
                  <Input
                    value={invoice.customer?.vat || ""}
                    onChange={(e) =>
                      setInvoice((p) => ({
                        ...p,
                        customer: { ...p.customer, vat: e.target.value },
                      }))
                    }
                    className="invoice-input"
                    placeholder="VAT number"
                  />
                </div>

                <div className="flex flex-col md:col-span-2 xl:col-span-3">
                  <label className="text-sm font-medium text-gray-700 mb-2">Bill To (Address)</label>
                  <Input.TextArea
                    rows={3}
                    value={invoice.customer?.address || ""}
                    onChange={(e) =>
                      setInvoice((p) => ({
                        ...p,
                        customer: { ...p.customer, address: e.target.value },
                      }))
                    }
                    className="invoice-textarea"
                    placeholder="Customer address"
                  />
                </div>

                <div className="flex flex-col md:col-span-2 xl:col-span-3">
                  <label className="text-sm font-medium text-gray-700 mb-2">Amounts Are</label>
                  <div className="flex flex-wrap items-center gap-3">
                    <Select
                      value={headerVatPercent === 0 ? "no_vat" : "vat_20"}
                      onChange={(val) => {
                        const pct = val === "no_vat" ? 0 : 20;
                        setHeaderVatPercent(pct);
                        applyHeaderVatToAllLines(pct);
                      }}
                      style={{ width: 220 }}
                      className="invoice-input"
                    >
                      <Option value="no_vat">No VAT</Option>
                      <Option value="vat_20">VAT 20%</Option>
                    </Select>

                    <InputNumber
                      min={0}
                      max={100}
                      value={headerVatPercent}
                      disabled={headerVatPercent === 0}
                      formatter={(v) => `${v}%`}
                      parser={(v) => Number(String(v).replace("%", ""))}
                      onChange={(n) => {
                        const pct = Number(n || 0);
                        setHeaderVatPercent(pct);
                        applyHeaderVatToAllLines(pct);
                      }}
                      className="invoice-input"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden mb-6">
              <div className="p-6 border-b border-gray-200">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 mb-1">Invoice Items</h2>
                    <p className="text-sm text-gray-500">
                      Add manual rows, inventory items or production items
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button
                      icon={<PlusOutlined />}
                      onClick={addManualRow}
                      className="rounded-lg font-medium"
                    >
                      Add Row
                    </Button>

                    <Button
                      onClick={openProductionPicker}
                      className="rounded-lg font-medium"
                    >
                      Add from Production
                    </Button>

                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => setPartModalOpen(true)}
                      className="bg-blue-500 hover:bg-blue-600 border-blue-500 text-white rounded-lg font-medium"
                    >
                      Add From Inventory
                    </Button>
                  </div>
                </div>
              </div>

              <Table
                dataSource={invoice.lines.map((x, i) => ({ key: i, ...x }))}
                columns={columns}
                pagination={false}
                size="middle"
                bordered={false}
                loading={loading}
                scroll={{ x: "max-content" }}
                className="modern-invoice-table"
                locale={{
                  emptyText: (
                    <div className="text-center py-10">
                      <div className="text-lg font-medium text-gray-500 mb-2">
                        No invoice items yet
                      </div>
                      <div className="text-sm text-gray-400">
                        Start by adding a row, production item, or inventory item
                      </div>
                    </div>
                  ),
                }}
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-1">Notes</h2>
                <p className="text-sm text-gray-500 mb-4">
                  Optional invoice notes for PDF or customer reference
                </p>
                <Input.TextArea
                  rows={5}
                  value={invoice.note}
                  onChange={(e) => setInvoice((p) => ({ ...p, note: e.target.value }))}
                  className="invoice-textarea"
                  placeholder="Add invoice note..."
                />
              </div>

              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Invoice Summary</h2>

                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="font-medium text-gray-900">
                      {formatCurrency(totals.subtotal)}
                    </span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-600">Total VAT</span>
                    <span className="font-medium text-gray-900">
                      {formatCurrency(totals.tax)}
                    </span>
                  </div>

                  <div className="flex justify-between items-center pt-3">
                    <span className="text-lg font-semibold text-gray-900">Total</span>
                    <span className="text-xl font-bold text-blue-600">
                      {formatCurrency(totals.grand)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Modal
            title="Add From Inventory"
            open={partModalOpen}
            onCancel={() => {
              setPartModalOpen(false);
              setAddingPart({ productId: null, qty: 1, price: 0 });
            }}
            onOk={addInventoryPart}
            okText="Add"
            className="modern-modal"
          >
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Part</label>
                <Select
                  value={addingPart.productId}
                  onChange={(v) => setAddingPart((p) => ({ ...p, productId: v }))}
                  style={{ width: "100%" }}
                  placeholder="Select part"
                  showSearch
                  optionFilterProp="children"
                  className="invoice-input"
                >
                  {inventoryParts.map((p) => (
                    <Option key={p.id} value={p.id}>
                      {p.product_name}
                    </Option>
                  ))}
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Qty</label>
                <InputNumber
                  min={1}
                  value={addingPart.qty}
                  onChange={(n) => setAddingPart((p) => ({ ...p, qty: n }))}
                  style={{ width: "100%" }}
                  className="invoice-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Unit Price</label>
                <InputNumber
                  min={0}
                  value={addingPart.price}
                  onChange={(n) => setAddingPart((p) => ({ ...p, price: n }))}
                  style={{ width: "100%" }}
                  className="invoice-input"
                />
              </div>
            </div>
          </Modal>

          <Modal
            title="Select Production Orders"
            open={prodModalOpen}
            onCancel={() => setProdModalOpen(false)}
            onOk={addSelectedProductionToInvoice}
            okText={`Add Selected (${prodSelectedRowKeys.filter((id) => !prodExistingSet.has(Number(id))).length})`}
            width={980}
            className="modern-modal"
          >
            <Spin spinning={prodLoading}>
              <Table
                rowKey="id"
                dataSource={prodOrders}
                pagination={{ pageSize: 10 }}
               rowSelection={{
  selectedRowKeys: prodSelectedRowKeys,
  onChange: (keys) => {
    const cleanKeys = keys.filter((key) => {
      const row = prodOrders.find((r) => Number(r.id) === Number(key));
      return row && !row.alreadyInvoiced && !prodExistingSet.has(Number(row.id));
    });

    const union = new Set([...prodExistingIds, ...cleanKeys]);
    setProdSelectedRowKeys([...union]);
  },
  getCheckboxProps: (record) => ({
    disabled:
      prodExistingSet.has(Number(record.id)) || record.alreadyInvoiced,
  }),
}}
                className="modern-invoice-table"
                // Replace the columns section inside the Select Production Orders modal with this
columns={[
  {
    title: "No",
    key: "seq_no",
    width: 80,
    render: (_, record) => {
      try {
        const display = computeDisplaySequence(record, prodOrders);
        return <span className="font-mono">{display}</span>;
      } catch {
        return <span className="font-mono">{String(record.id)}</span>;
      }
    },
  },
  { title: "Size", dataIndex: "size", width: 120 },
  { title: "Product", dataIndex: "product_name", width: 220 },
  {
    title: "Date / Month",
    key: "date_month",
    width: 150,
    render: (_, record) => (
      <span className="text-gray-700">{formatProductionDateOrMonth(record)}</span>
    ),
  },
  { title: "Color", dataIndex: "color", width: 140 },
  { title: "Storage", dataIndex: "storageType", width: 140 },
  {
    title: "Qty",
    dataIndex: "quantity",
    width: 80,
    render: (q) => <span className="font-semibold">{q}</span>,
  },
  {
    title: "Invoice Status",
    key: "invoiceStatus",
    width: 140,
    render: (_, record) => (
      <span className={record.alreadyInvoiced ? "text-red-500" : "text-green-600"}>
        {record.alreadyInvoiced ? "Invoiced" : "Available"}
      </span>
    ),
  },
  {
    title: "Unit Price (£)",
    dataIndex: "price",
    width: 120,
    render: (v) => <span>{v != null ? formatCurrency(v) : "-"}</span>,
  },
  {
    title: "Line Total",
    dataIndex: "lineTotal",
    width: 120,
    render: (v) => <span>{v != null ? formatCurrency(v) : "-"}</span>,
  },
]}
              />
            </Spin>
          </Modal>

          <Modal
            title="Add Contact"
            open={contactModalOpen}
            onCancel={() => setContactModalOpen(false)}
            onOk={saveContact}
            okText="Save Contact"
            className="modern-modal"
          >
            <Form form={contactForm} layout="vertical">
              <Form.Item
                label="Name / Company"
                name="name"
                rules={[{ required: true, message: "Please enter a name" }]}
              >
                <Input className="invoice-input" />
              </Form.Item>

              <Form.Item label="Address" name="address">
                <Input.TextArea rows={3} className="invoice-textarea" />
              </Form.Item>

              <Form.Item
                label="Email"
                name="email"
                rules={[{ type: "email", message: "Please enter a valid email address" }]}
              >
                <Input placeholder="Enter email address" className="invoice-input" />
              </Form.Item>

              <Form.Item label="VAT Number" name="vat">
                <Input className="invoice-input" />
              </Form.Item>
            </Form>
          </Modal>
        </div>
      </div>

      <style>{`
        .invoice-input.ant-input,
        .invoice-input .ant-select-selector,
        .invoice-input.ant-picker,
        .invoice-input.ant-input-number,
        .invoice-textarea.ant-input {
          border-radius: 10px !important;
        }

        .modern-invoice-table .ant-table-thead > tr > th {
          background: #f8fafc !important;
          color: #374151 !important;
          font-weight: 600;
          border-bottom: 2px solid #e5e7eb !important;
          white-space: nowrap;
          padding: 12px 16px !important;
        }

        .modern-invoice-table .ant-table-tbody > tr > td {
          border-bottom: 1px solid #f1f5f9 !important;
          padding: 12px 16px !important;
          transition: all 0.2s ease;
          background: #fff;
        }

        .modern-invoice-table .ant-table-tbody > tr:hover > td {
          background: #f9fafb !important;
        }

        .modern-invoice-table .ant-table-container {
          border: none !important;
        }

        .modern-invoice-table .ant-table {
          border-radius: 0 !important;
        }

        .modern-modal .ant-modal-content {
          border-radius: 16px;
          overflow: hidden;
        }

        .modern-modal .ant-modal-header {
          border-bottom: 1px solid #e5e7eb;
          background: #f8fafc;
        }
      `}</style>
    </Spin>
  );
};

export default InvoiceManager;